import {
  doc, getDoc, setDoc, deleteDoc, updateDoc,
  collection, query, where, orderBy, limit as firestoreLimit, startAfter,
  getDocs, runTransaction, increment, serverTimestamp,
  collectionGroup, Timestamp
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { generateInviteToken } from '../utils/inviteToken';
import { generateUniqueSlug } from '../utils/slugify';
import { getEventUrl, getPublicBaseUrl } from '../utils/publicUrl';
import { useEventsStore } from '../stores/useEventsStore';
import { firestoreService } from './firestore.service';
import analyticsService from './analytics.service';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

export function normalizeInviteEmails(emails = []) {
  const source = Array.isArray(emails) ? emails : [emails];
  const unique = [];
  const seen = new Set();

  for (const raw of source) {
    const normalized = normalizeEmail(raw);
    if (!normalized || !EMAIL_RE.test(normalized) || seen.has(normalized)) continue;
    seen.add(normalized);
    unique.push(normalized);
  }

  return unique;
}

async function ensureShareableEvent(event) {
  if (!event?.id) {
    throw new Error('Event must have an id to share');
  }

  const user = auth.currentUser;
  const visibility = event.visibility || 'private';
  const hostId = event.hostId || user?.uid || null;
  const hostDisplayName = event.hostDisplayName || user?.displayName || null;
  const hostPhotoURL = event.hostPhotoURL || user?.photoURL || null;
  const hostEmail = event.hostEmail || user?.email || null;

  if (!hostId) {
    throw new Error('You must be signed in to share events.');
  }
  if (visibility === 'private') {
    throw new Error('Cannot publish a private event');
  }

  const updates = {
    hostId,
    hostDisplayName,
    hostPhotoURL,
    hostEmail,
    invitedEmails: normalizeInviteEmails(event.invitedEmails || []),
  };

  if (!event.slug && (visibility === 'public' || visibility === 'invite-only')) {
    const year = event.start ? new Date(event.start).getFullYear() : new Date().getFullYear();
    updates.slug = await generateUniqueSlug(event.title || event.name || 'event', year);
  }

  if (!event.inviteToken && visibility === 'invite-only') {
    updates.inviteToken = generateInviteToken();
  }

  return {
    ...event,
    visibility,
    ...updates,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Build the shared_events document from an event object.
 * Picks only the fields needed for the public page.
 */
function buildSharedEventDoc(event) {
  const toDate = (value, fallback = new Date()) => {
    if (value?.toDate && typeof value.toDate === 'function') {
      return value.toDate();
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? fallback : parsed;
  };

  return {
    id: event.id,
    title: event.title || '',
    description: event.description || null,
    location: event.location || null,
    start: Timestamp.fromDate(toDate(event.start)),
    end: Timestamp.fromDate(toDate(event.end, toDate(event.start))),
    isAllDay: event.isAllDay || false,

    emoji: event.emoji || null,
    image: event.image || null,
    vibe: event.vibe || null,
    font: event.font || null,
    category: event.category || null,
    color: event.color || null,
    textcolor: event.textcolor || null,

    visibility: event.visibility,
    slug: event.slug || null,
    inviteToken: event.inviteToken || null,
    shareCardStyle: event.shareCardStyle || null,
    ogImageUrl: event.ogImageUrl || null,
    invitedEmails: normalizeInviteEmails(event.invitedEmails || []),

    hostId: event.hostId,
    hostDisplayName: event.hostDisplayName || null,
    hostPhotoURL: event.hostPhotoURL || null,
    hostEmail: event.hostEmail || null,

    rsvpEnabled: event.rsvpEnabled || false,
    maxAttendees: event.maxAttendees || null,

    // Google Calendar metadata for outbound attendee sync
    googleCalendarId: event.calendar || null,
    googleAccountEmail: event.primary_email || null,

    createdAt: event.createdAt ? Timestamp.fromDate(toDate(event.createdAt)) : Timestamp.now(),
    updatedAt: Timestamp.now(),
    publishedAt: Timestamp.now(),
  };
}

/**
 * Publish an event to the shared_events collection.
 * Creates or overwrites the document. Called when visibility
 * is switched to 'public' or 'invite-only'.
 *
 * @param {Object} event - The full event object from the Zustand store
 * @returns {Promise<Object>} The normalized shared event payload
 */
export async function publishEvent(event) {
  const prepared = await ensureShareableEvent(event);
  const docData = buildSharedEventDoc(prepared);
  await setDoc(doc(db, 'shared_events', prepared.id), docData, { merge: true });
  return prepared;
}

/**
 * Keep shared_events in sync with local event edits.
 * Private events are unpublished; shared events are re-published/updated.
 *
 * @param {Object} event
 * @returns {Promise<Object>} The normalized event used for syncing
 */
export async function syncSharedEventForEvent(event) {
  if (!event?.id) return event;
  const visibility = event.visibility || 'private';

  if (visibility === 'private') {
    await unpublishEvent(event.id).catch(() => {});
    return { ...event, visibility: 'private' };
  }

  return publishEvent(event);
}

/**
 * Unpublish an event — remove it from shared_events.
 * Called when visibility is switched to 'private'.
 *
 * @param {string} eventId
 * @returns {Promise<void>}
 */
export async function unpublishEvent(eventId) {
  await deleteDoc(doc(db, 'shared_events', eventId));
}

/**
 * Update specific fields on a shared event document.
 *
 * @param {string} eventId
 * @param {Object} updates
 * @returns {Promise<void>}
 */
export async function updateSharedEvent(eventId, updates) {
  if (!eventId || !updates) return;
  const normalized = { ...updates };
  if (Object.hasOwn(normalized, 'invitedEmails')) {
    normalized.invitedEmails = normalizeInviteEmails(normalized.invitedEmails || []);
  }
  await updateDoc(doc(db, 'shared_events', eventId), {
    ...normalized,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Fetch a shared event by its public slug.
 *
 * @param {string} slug
 * @returns {Promise<Object|null>} The shared event data or null
 */
export async function getSharedEventBySlug(slug) {
  const q = query(
    collection(db, 'shared_events'),
    where('slug', '==', slug)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
}

/**
 * Fetch a shared event by its invite token.
 *
 * @param {string} token
 * @returns {Promise<Object|null>} The shared event data or null
 */
export async function getSharedEventByToken(token) {
  const q = query(
    collection(db, 'shared_events'),
    where('inviteToken', '==', token)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
}

/**
 * Regenerate the invite token for an event.
 * Updates both local state and Firestore shared_events document.
 * Old token is immediately invalidated.
 *
 * @param {string} eventId - The event's ID
 * @returns {Promise<string>} The new invite token
 */
export async function regenerateInviteToken(eventId) {
  const newToken = generateInviteToken();
  const { events: existingEvents } = useEventsStore.getState();
  const previous = existingEvents.find(e => e.id === eventId);
  const previousToken = previous?.inviteToken || null;
  const now = new Date().toISOString();

  // Update local Zustand store
  const { events, setEvents } = useEventsStore.getState();
  setEvents(events.map(e =>
    e.id === eventId
      ? { ...e, inviteToken: newToken, updatedAt: now }
      : e
  ));

  // Update Firestore shared_events document
  try {
    const sharedEventRef = doc(db, 'shared_events', eventId);
    await updateDoc(sharedEventRef, {
      inviteToken: newToken,
      updatedAt: now,
    });
  } catch (error) {
    console.error('Failed to update invite token in Firestore:', error);
    // Revert local state on failure
    const { events: currentEvents, setEvents: setCurrentEvents } = useEventsStore.getState();
    setCurrentEvents(currentEvents.map(e =>
      e.id === eventId
        ? { ...e, inviteToken: previousToken, updatedAt: now }
        : e
    ));
    throw error;
  }

  return newToken;
}

/**
 * Queue invite emails and in-app notifications for invited guests.
 *
 * @param {Object} event - Event payload (must include id/title/visibility)
 * @param {string[]} inviteEmails
 * @param {{ inviteMessage?: string }} [options]
 * @returns {Promise<{queued: number, failed: number, notified: number}>}
 */
export async function queueEventInvites(event, inviteEmails, options = {}) {
  const normalizedEmails = normalizeInviteEmails(inviteEmails);
  if (!event?.id || normalizedEmails.length === 0) {
    return { queued: 0, failed: 0, notified: 0 };
  }

  const resolvedVisibility = (event.visibility || 'private') === 'private'
    ? 'invite-only'
    : (event.visibility || 'private');

  const preparedEvent = await syncSharedEventForEvent({
    ...event,
    visibility: resolvedVisibility,
    invitedEmails: normalizeInviteEmails([
      ...(event.invitedEmails || []),
      ...normalizedEmails,
    ]),
  });

  const inviter = auth.currentUser;
  const inviterName = inviter?.displayName || preparedEvent.hostDisplayName || 'Someone';
  const startDate = preparedEvent.start?.toDate
    ? preparedEvent.start.toDate()
    : new Date(preparedEvent.start);
  const eventDate = preparedEvent.start && !Number.isNaN(startDate.getTime())
    ? startDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const baseUrl = getPublicBaseUrl();
  const eventUrl = getEventUrl(preparedEvent, baseUrl);

  let queued = 0;
  let failed = 0;
  let notified = 0;

  try {
    const { queueNotificationEmail } = await import('./email.service');
    const emailResults = await Promise.all(
      normalizedEmails.map(email => queueNotificationEmail(email, 'event_invite', {
        recipientName: email.split('@')[0],
        fromDisplayName: inviterName,
        eventTitle: preparedEvent.title || preparedEvent.name || 'an event',
        eventDate,
        eventLocation: preparedEvent.location || null,
        eventUrl,
        inviteMessage: options.inviteMessage || '',
      }))
    );
    queued = emailResults.filter(Boolean).length;
    failed = normalizedEmails.length - queued;
  } catch (error) {
    console.error('Failed to queue invite emails:', error);
    failed = normalizedEmails.length;
  }

  try {
    const knownUsers = await firestoreService.getUsersByEmails(normalizedEmails);
    const { createNotification } = await import('./notification.service');
    const notifyResults = await Promise.all(
      Object.values(knownUsers).map(user =>
        createNotification(user.userId, {
          type: 'event_invite',
          eventId: preparedEvent.id,
          eventSlug: preparedEvent.slug || null,
          eventInviteToken: preparedEvent.visibility === 'invite-only'
            ? (preparedEvent.inviteToken || null)
            : null,
          eventTitle: preparedEvent.title || preparedEvent.name || 'an event',
          fromUserId: inviter?.uid || preparedEvent.hostId || null,
          fromDisplayName: inviterName,
          fromPhotoURL: inviter?.photoURL || preparedEvent.hostPhotoURL || null,
          title: 'You were invited',
          body: `${inviterName} invited you to ${preparedEvent.title || preparedEvent.name || 'an event'}`,
        })
      )
    );
    notified = notifyResults.filter(Boolean).length;
  } catch (error) {
    console.error('Failed to create invite notifications:', error);
  }

  analyticsService.trackInviteAttendeeAdded(preparedEvent.title || preparedEvent.name || '', normalizedEmails.length);

  return { queued, failed, notified };
}

// ============ RSVP OPERATIONS ============

const RSVP_COLLECTION = 'event_rsvps';
const RESPONSES_SUB = 'responses';
const SHARED_EVENTS_COLLECTION = 'shared_events';
const RSVP_SAMPLE_MAX = 5;

/**
 * Create or update the current user's RSVP for a shared event.
 * Uses a transaction to atomically update the RSVP doc and denormalized counters.
 *
 * @param {string} eventId
 * @param {'going'|'not_going'} status
 * @param {'web'|'invite_link'} [source='web']
 * @returns {Promise<void>}
 */
export async function rsvpToEvent(eventId, status, source = 'web') {
  const user = auth.currentUser;
  if (!user) throw new Error('Must be authenticated to RSVP');
  if (!['going', 'not_going'].includes(status)) throw new Error('Invalid RSVP status');

  const rsvpRef = doc(db, RSVP_COLLECTION, eventId, RESPONSES_SUB, user.uid);
  const sharedEventRef = doc(db, SHARED_EVENTS_COLLECTION, eventId);

  let previousStatus = null;
  let isNewRsvp = false;

  await runTransaction(db, async (transaction) => {
    const existingRsvp = await transaction.get(rsvpRef);
    const sharedEvent = await transaction.get(sharedEventRef);

    previousStatus = existingRsvp.exists() ? existingRsvp.data().status : null;
    isNewRsvp = !existingRsvp.exists();

    // No change needed
    if (previousStatus === status) return;

    // Build RSVP document
    const rsvpData = {
      userId: user.uid,
      displayName: user.displayName || 'Anonymous',
      photoURL: user.photoURL || null,
      email: user.email,
      status,
      updatedAt: serverTimestamp(),
      source,
      googleCalendarSynced: false,
    };

    if (!existingRsvp.exists()) {
      rsvpData.rsvpAt = serverTimestamp();
      transaction.set(rsvpRef, rsvpData);
    } else {
      transaction.update(rsvpRef, rsvpData);
    }

    // Calculate counter deltas
    const counterUpdates = {};
    if (status === 'going') counterUpdates['rsvpCounts.going'] = increment(1);
    if (status === 'not_going') counterUpdates['rsvpCounts.not_going'] = increment(1);
    if (previousStatus === 'going') counterUpdates['rsvpCounts.going'] = increment(-1);
    if (previousStatus === 'not_going') counterUpdates['rsvpCounts.not_going'] = increment(-1);

    // Update rsvpSample array
    const userSample = { userId: user.uid, displayName: user.displayName || 'Anonymous', photoURL: user.photoURL || null };

    if (sharedEvent.exists()) {
      const data = sharedEvent.data();
      let sample = data.rsvpSample || [];

      if (status === 'going' && !sample.some(s => s.userId === user.uid) && sample.length < RSVP_SAMPLE_MAX) {
        sample = [...sample, userSample];
        counterUpdates.rsvpSample = sample;
      } else if (status === 'not_going') {
        const filtered = sample.filter(s => s.userId !== user.uid);
        if (filtered.length !== sample.length) {
          counterUpdates.rsvpSample = filtered;
        }
      }

      transaction.update(sharedEventRef, counterUpdates);
    } else {
      // Create shared_events doc with initial counters if it doesn't exist
      transaction.set(sharedEventRef, {
        rsvpCounts: { going: status === 'going' ? 1 : 0, not_going: status === 'not_going' ? 1 : 0 },
        rsvpSample: status === 'going' ? [userSample] : [],
      }, { merge: true });
    }
  });

  // Fire-and-forget analytics after successful transaction
  if (previousStatus !== status) {
    if (isNewRsvp) {
      analyticsService.trackRsvpCreated(eventId, '', source);
    } else {
      analyticsService.trackRsvpUpdated(eventId, previousStatus, status);
      if (status === 'not_going') {
        analyticsService.trackRsvpCancelled(eventId);
      }
    }
  }

  // Fire-and-forget outbound Google Calendar sync (debounced batch)
  // Only succeeds if the current user is the event host (has the Google token)
  import('./rsvp.service').then(({ triggerOutboundSync }) => {
    triggerOutboundSync(eventId, {
      email: user.email,
      status,
      displayName: user.displayName,
    }).catch(() => {});
  }).catch(() => {});

  // Fire-and-forget host notification (Phase 6)
  if (previousStatus !== status) {
    const notifType = status === 'going' ? 'rsvp_received' : 'rsvp_cancelled';
    import('./notification.service').then(({ notifyHost }) => {
      notifyHost(eventId, notifType, {
        uid: user.uid,
        displayName: user.displayName,
        photoURL: user.photoURL,
      }).catch(() => {});
    }).catch(() => {});
  }
}

/**
 * Cancel the current user's RSVP (sets status to 'not_going').
 *
 * @param {string} eventId
 * @returns {Promise<void>}
 */
export async function cancelRsvp(eventId) {
  return rsvpToEvent(eventId, 'not_going');
}

/**
 * Get paginated RSVP responses for a given event (going users only).
 *
 * @param {string} eventId
 * @param {{ limit?: number, cursor?: DocumentSnapshot }} options
 * @returns {Promise<{ rsvps: Object[], cursor: DocumentSnapshot|null, hasMore: boolean }>}
 */
export async function getEventRsvps(eventId, { limit: pageSize = 20, cursor = null } = {}) {
  const responsesRef = collection(db, RSVP_COLLECTION, eventId, RESPONSES_SUB);
  const constraints = [where('status', '==', 'going'), orderBy('rsvpAt', 'desc'), firestoreLimit(pageSize)];

  if (cursor) {
    constraints.push(startAfter(cursor));
  }

  const q = query(responsesRef, ...constraints);
  const snapshot = await getDocs(q);
  const rsvps = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;

  return { rsvps, cursor: lastDoc, hasMore: snapshot.docs.length === pageSize };
}

/**
 * Get the current user's RSVPs across all events (collection group query).
 *
 * @param {{ limit?: number, cursor?: DocumentSnapshot }} options
 * @returns {Promise<{ rsvps: Object[], cursor: DocumentSnapshot|null, hasMore: boolean }>}
 */
export async function getMyRsvps({ limit: pageSize = 20, cursor = null } = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error('Must be authenticated to get RSVPs');

  const constraints = [
    where('userId', '==', user.uid),
    where('status', '==', 'going'),
    orderBy('rsvpAt', 'desc'),
    firestoreLimit(pageSize),
  ];

  if (cursor) {
    constraints.push(startAfter(cursor));
  }

  const q = query(collectionGroup(db, RESPONSES_SUB), ...constraints);
  const snapshot = await getDocs(q);
  const rsvps = snapshot.docs.map(d => ({ id: d.id, eventId: d.ref.parent.parent.id, ...d.data() }));
  const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;

  return { rsvps, cursor: lastDoc, hasMore: snapshot.docs.length === pageSize };
}

/**
 * Get a single user's RSVP for a specific event.
 *
 * @param {string} eventId
 * @param {string} [userId] - defaults to current user
 * @returns {Promise<Object|null>}
 */
/**
 * Get a shared event by its document ID.
 *
 * @param {string} eventId
 * @returns {Promise<Object|null>}
 */
export async function getSharedEvent(eventId) {
  const ref = doc(db, SHARED_EVENTS_COLLECTION, eventId);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function getUserRsvp(eventId, userId) {
  const uid = userId || auth.currentUser?.uid;
  if (!uid) return null;

  const rsvpRef = doc(db, RSVP_COLLECTION, eventId, RESPONSES_SUB, uid);
  const snap = await getDoc(rsvpRef);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}
